"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { BarChart3, DollarSign, CalendarDays, FileText, TrendingUp, CheckCircle } from "lucide-react"

export function DashboardShowcase() {
  const dashboardFeatures = [
    {
      title: "Comprehensive Analytics Dashboard",
      description:
        "Real-time insights into appointments, revenue, patient flow, and clinic performance with AI-powered recommendations.",
      image: "/images/dashboard-overview.png",
      icon: BarChart3,
      features: ["Revenue Tracking", "Appointment Analytics", "Patient Demographics", "Specialty Distribution"],
    },
    {
      title: "Intelligent Expense Management",
      description:
        "AI-powered expense tracking and budget optimization with predictive analytics and automated categorization.",
      image: "/images/expense-management.png",
      icon: DollarSign,
      features: ["Automated Categorization", "Budget Alerts", "Expense Trends", "Cost Optimization"],
    },
    {
      title: "Advanced Financial Analytics",
      description:
        "Deep financial insights with doctor performance metrics, service revenue analysis, and profitability tracking.",
      image: "/images/financial-management.png",
      icon: TrendingUp,
      features: ["Doctor Performance", "Service Analytics", "Revenue Optimization", "Financial Forecasting"],
    },
    {
      title: "Smart Appointment Management",
      description:
        "AI-optimized scheduling system with automated reminders, conflict detection, and patient flow optimization.",
      image: "/images/appointments.png",
      icon: CalendarDays,
      features: ["Smart Scheduling", "Automated Reminders", "Status Tracking", "Conflict Resolution"],
    },
    {
      title: "Comprehensive Patient History",
      description:
        "Complete patient journey tracking with AI-organized medical records, treatment history, and predictive health insights.",
      image: "/images/patient-history.png",
      icon: FileText,
      features: ["Medical History", "Treatment Tracking", "Prescription Management", "Health Predictions"],
    },
  ]

  return (
    <section className="w-full py-24 bg-gray-50">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4">
            Platform Overview
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">See Clinic365 AI in Action</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the power of artificial intelligence in healthcare management. Our platform learns from your
            clinic's patterns to optimize operations and improve patient outcomes.
          </p>
        </div>

        <div className="space-y-24">
          {dashboardFeatures.map((feature, index) => (
            <div
              key={index}
              className={`grid gap-12 lg:grid-cols-2 items-center ${index % 2 === 1 ? "lg:grid-cols-2" : ""}`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="inline-flex p-3 bg-blue-600 rounded-lg">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">{feature.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  {feature.features.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <Card className="bg-white border shadow-xl overflow-hidden">
                  <CardContent className="p-0">
                    <img src={feature.image || "/placeholder.svg"} alt={feature.title} className="w-full h-auto" />
                  </CardContent>
                </Card>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
